import React, { ReactElement } from 'react';
import { pipe } from 'fp-ts/lib/function';
import { UseFormGetValues } from 'react-hook-form';
import { Button, Cell, Row } from '@dexma/ui-components';
import { WidgetOptionValues } from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Save/useSaveWidgetContainer';
import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import { OPTIONS } from 'adapter/in/common/widget/options';
import { StyledResetButton } from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Group/style';
import {
    StyledRangeContainer,
    StyledRemoveElementContainer,
    StyledResetRangeContainer,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Range/style';
import useRangeContainer from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Range/useRangeContainer';
import RangeGroupContainer from 'adapter/in/dashboards/pages/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/OptionForm/Range';
import { asWidgetGaugeRanges, JsonGaugeRange } from 'adapter/out/client/widgets/data/mappers/GaugeMapper';
import i18n from 'asset/translation';
import Widget from 'domain/entity/widget';
import { ColorType } from 'domain/entity/widgetData';
import GaugeData, { WidgetGaugeRange } from 'domain/entity/widgets/GaugeData';
import { ConfigFieldType, FieldStatus, FormFields, GaugeRangeValue } from 'adapter/in/common/form/types';

export const asRangeValues = (
    key: string,
    value: GaugeRangeValue[]
): {
    key: string;
    value: JsonGaugeRange[];
} => ({
    key,
    value: value.map((range) => ({
        name: range.name.value,
        color: {
            type: ColorType.BY_DEFAULT,
            by_default: range.color.value,
        },
        from: Number(range.from.value),
        to: Number(range.to.value),
    })),
});

export const populateRangesValues = (getValues: UseFormGetValues<FormFields>): WidgetOptionValues[] => {
    if (getValues(OPTIONS.RANGES) !== undefined) {
        const arrayValues = pipe(getValues(OPTIONS.RANGES), (ranges) => rangesHaveOptionalData((ranges as unknown) as GaugeRangeValue[]));
        if (arrayValues.length > 0) {
            return [asRangeValues(OPTIONS.RANGES, (arrayValues as unknown) as GaugeRangeValue[])];
        }
    }
    return [];
};

export const rangesHaveOptionalData = (ranges: GaugeRangeValue[]): GaugeRangeValue[] => {
    const haveOptionalData =
        ranges
            .map((range) => Object.values(range))
            .flat()
            .filter((value) => value.status === FieldStatus.HAS_VALUE).length > 0;
    return haveOptionalData ? ranges : [];
};

type RangeMapperObject = {
    isDefaultData: boolean;
    ranges: WidgetGaugeRange[];
};

export const getCustomRanges = (widget: Widget, widgetData?: GaugeData): RangeMapperObject => {
    if (widgetData && widget.optionFields.filter((option) => option.key === OPTIONS.RANGES).length === 0) {
        return { isDefaultData: true, ranges: widgetData.ranges };
    }
    return {
        isDefaultData: false,
        ranges: asWidgetGaugeRanges(
            widget.optionFields.filter((option) => option.key === OPTIONS.RANGES).length > 0
                ? ((widget.optionFields.filter((option) => option.key === OPTIONS.RANGES)[0].value as unknown) as JsonGaugeRange[])
                : []
        ),
    };
};

export const mapCustomRangeFieldData = ({ isDefaultData, ranges }: RangeMapperObject): GaugeRangeValue[] => {
    const status = isDefaultData ? FieldStatus.DEFAULT_DATA : FieldStatus.HAS_VALUE;
    return ranges.map((range) => ({
        name: buildTextInputFieldData(status, range.name, ConfigFieldType.OPTIONS, range.name),
        color: buildTextInputFieldData(status, range.color.byDefault, ConfigFieldType.OPTIONS, range.color.byDefault),
        from: buildTextInputFieldData(status, String(range.from), ConfigFieldType.OPTIONS, String(range.from)),
        to: buildTextInputFieldData(status, String(range.to), ConfigFieldType.OPTIONS, String(range.to)),
    }));
};

type RangeContainerProps = {
    data: GaugeData;
    title: string;
};

const RangeContainer = ({ data, title }: RangeContainerProps): ReactElement => {
    const { fields, getValue, showReset, handleAddRange, handleResetRanges, handleRemoveRange } = useRangeContainer(OPTIONS.RANGES, data);

    return (
        <StyledRangeContainer key='ranges'>
            <Row>
                <Cell className='widget-type-block'>
                    <span>{title}</span>
                </Cell>
                {showReset && (
                    <StyledResetRangeContainer>
                        <StyledResetButton
                            type='button'
                            variant='link'
                            text={i18n.t('widgets.widgetMenu.resetDefaultData')}
                            data-testid='cancel'
                            onClick={handleResetRanges}
                        />
                    </StyledResetRangeContainer>
                )}
            </Row>
            {fields.length > 0 &&
                fields.map((item, index) => (
                    <RangeGroupContainer
                        key={item.id}
                        item={item}
                        index={index}
                        handleRemoveRange={handleRemoveRange}
                        getValue={getValue}
                        count={fields.length}
                    />
                ))}
            <Row>
                <StyledRemoveElementContainer>
                    <Button
                        kind='iconTextButton'
                        iconBefore='add'
                        text={i18n.t('widgets.widgetMenu.ranges.add')}
                        variant='outline'
                        onClick={handleAddRange}
                    />
                </StyledRemoveElementContainer>
            </Row>
        </StyledRangeContainer>
    );
};

export default RangeContainer;
